import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  FormControl,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from "@mui/material";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DescriptionIcon from "@mui/icons-material/Description";
import FileFilters from "./FileFilters";
import { fetchFileListing } from "../services/apiService";

const DocumentFiltersDrawer = ({
  open,
  sx,
  onDrawerExpandedChange,
  setFilterDict,
  isDisabled,
  isAuthenticated,
}) => {
  const theme = useTheme();
  const [availableFiles, setAvailableFiles] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [product_categoryList, setProduct_categoryList] = useState([]);
  const [sourcesList, setSourcesList] = useState([]);
  const [tocList, setTOCList] = useState([]);
  const [yearList, setYearList] = useState([]);
  const [, setIsLoading] = useState(true);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [filtersAccordionExpanded, setFiltersAccordionExpanded] = useState(true);
  const [filterPayload, setFilterPayload] = useState({ filters: {} });
  const pollingIntervalRef = useRef(null);

  const handleFilterChange = (payload) => {
    const safePayload = payload || { filters: {} };
    setFilterPayload(safePayload);
    setFilterDict(safePayload.filters || {});
  };

  const toggleDrawerExpansion = (event) => {
    event.stopPropagation();
    setDrawerExpanded((prev) => !prev);
  };

  const fetchAvailableFiles = useCallback(async () => {
    if (!localStorage.getItem("accessToken")) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchFileListing();

      let files = [];
      let category = [];
      let product_category = [];
      let brand = [];
      let sources = [];
      let toc = [];
      let year = [];

      if (response.data && Array.isArray(response.data)) {
        files = response.data
          .filter((item) => item.status === "completed")
          .map((item) => item.filename);
        category = response.data
          .filter((item) => item.status === "completed")
          .map((item) => item.category);
        product_category = response.data
          .filter((item) => item.status === "completed")
          .map((item) => item.product_category);
        brand = response.data
          .filter((item) => item.status === "completed")
          .map((item) => item.brand);
        sources = response.data
          .filter((item) => item.status === "completed")
          .map((item) => item.sources);
        toc = response.data
          .filter((item) => item.status === "completed")
          .map((item) => item.toc);
        year = response.data
          .filter((item) => item.status === "completed")
          .map((item) => item.year);
      } else {
        files = response.filenames || [];
        category = response.category || [];
        product_category = response.product_category || [];
        brand = response.brand || [];
        sources = response.sources || [];
        toc = response.toc || [];
        year = response.year || [];
      }

      setAvailableFiles(files);
      setBrandList(brand);
      setCategoryList(category);
      setProduct_categoryList(product_category);
      setSourcesList(sources);
      setTOCList(toc);
      setYearList(year);
    } catch (error) {
      console.error("Failed to fetch available files:", error);
      setAvailableFiles([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAvailableFiles();
    }
  }, [fetchAvailableFiles, isAuthenticated]);

  useEffect(() => {
    if (open) {
      fetchAvailableFiles();

      if (!pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(fetchAvailableFiles, 60000);
      }

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, [fetchAvailableFiles, open]);

  useEffect(() => {
    if (onDrawerExpandedChange) {
      onDrawerExpandedChange(drawerExpanded);
    }
  }, [drawerExpanded, onDrawerExpandedChange]);

  return (
    <Paper
      elevation={0}
      sx={{
        position: "fixed",
        right: 0,
        top: 80,
        bottom: 0,
        width: {
          xs: open ? (drawerExpanded ? "100%" : "60px") : 0,
          sm: open ? (drawerExpanded ? "325px" : "60px") : 0,
        },
        height: "calc(100vh - 80px)",
        zIndex: 1,
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.3s ease-in-out, width 0.3s ease-in-out",
        borderRadius: 0,
        boxSizing: "border-box",
        overflow: "hidden",
        borderLeft: `1px solid ${theme.palette.divider}`,
        background: theme.palette.mode === "dark"
          ? "linear-gradient(180deg, rgba(18,26,51,0.96) 0%, rgba(10,16,36,0.98) 100%)"
          : "linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 100%)",
        boxShadow: open
          ? (theme.palette.mode === "dark"
            ? "-10px 0 30px rgba(0,0,0,0.45)"
            : "-8px 0 24px rgba(27,37,89,0.10)")
          : "none",
        ...sx,
      }}
    >
      <IconButton
        onClick={toggleDrawerExpansion}
        sx={{
          position: "absolute",
          left: drawerExpanded ? "auto" : "50%",
          right: drawerExpanded ? 10 : "auto",
          top: 10,
          transform: drawerExpanded ? "none" : "translateX(-50%)",
          width: 34,
          height: 34,
          borderRadius: "10px",
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.palette.mode === "dark"
            ? "0px 8px 20px rgba(0,0,0,0.35)"
            : "0px 4px 12px rgba(15,23,42,0.14)",
          border: `1px solid ${theme.palette.divider}`,
          zIndex: 2,
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "#F8FAFC",
          },
        }}
      >
        {drawerExpanded ? (
          <KeyboardDoubleArrowRightIcon
            sx={{
              fontSize: 18,
              color: theme.palette.mode === "dark" ? "#E2E8F0" : "#1E3A8A",
            }}
          />
        ) : (
          <KeyboardDoubleArrowLeftIcon
            sx={{
              fontSize: 18,
              color: theme.palette.mode === "dark" ? "#E2E8F0" : "#1E3A8A",
            }}
          />
        )}
      </IconButton>

      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          visibility: drawerExpanded ? "visible" : "hidden",
          opacity: drawerExpanded ? 1 : 0,
          transition: "visibility 0.2s, opacity 0.2s",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.92)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DescriptionIcon sx={{ fontSize: 18, color: "text.primary" }} />
            <Typography
              variant="subtitle1"
              sx={{ fontFamily: "'Poppins', sans-serif", color: "text.primary", fontWeight: 700 }}
            >
              Document Filters
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ mt: 0.5, display: "block", color: "text.secondary", fontFamily: "'Poppins', sans-serif" }}
          >
            Refine your document scope before sending a query
          </Typography>
        </Box>

        <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
          <FormControl sx={{ width: "100%" }}>
            <Accordion
              expanded={filtersAccordionExpanded}
              onChange={(_, expanded) => setFiltersAccordionExpanded(expanded)}
              disableGutters
              elevation={0}
              sx={{
                mt: 0,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "14px !important",
                backgroundColor: theme.palette.background.paper,
                "&:before": { display: "none" },
                "&.Mui-expanded": { mt: 1 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "text.primary" }} />}
                sx={{
                  borderRadius: "14px",
                  minHeight: 46,
                  px: 1.75,
                  "& .MuiAccordionSummary-content": { my: 0 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "6px",
                      backgroundColor: theme.palette.mode === "dark" ? "rgba(123,147,209,0.20)" : "rgba(27,37,89,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <DescriptionIcon sx={{ fontSize: 14, color: "text.primary" }} />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontFamily: "'Poppins', sans-serif", color: "text.primary", fontWeight: 600 }}
                  >
                    Filter Criteria
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.25, pt: 0.5, pb: 1.25 }}>
                {availableFiles.length === 0 ? (
                  <Box
                    sx={{
                      p: 2.5,
                      textAlign: "center",
                      backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(27,37,89,0.04)",
                      borderRadius: "10px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontFamily: "'Poppins', sans-serif" }}
                    >
                      No files available. Please upload a file to get started.
                    </Typography>
                  </Box>
                ) : (
                  <FileFilters
                    availableFiles={availableFiles}
                    brandList={brandList}
                    categoryList={categoryList}
                    product_categoryList={product_categoryList}
                    sourcesList={sourcesList}
                    tocList={tocList}
                    yearList={yearList}
                    onFilterChange={handleFilterChange}
                    disabled={isDisabled}
                    initialFilters={filterPayload.filters}
                    showHeader={false}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          </FormControl>
        </Box>
      </Box>

      {!drawerExpanded && (
        <Tooltip title="Document Filters" placement="left">
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(-90deg)",
              whiteSpace: "nowrap",
              color: "text.primary",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              letterSpacing: "0.2px",
              fontSize: "0.95rem",
              cursor: "pointer",
              overflow: "visible",
            }}
            onClick={toggleDrawerExpansion}
          >
            Document Filters
          </Box>
        </Tooltip>
      )}
    </Paper>
  );
};

DocumentFiltersDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  sx: PropTypes.object,
  onDrawerExpandedChange: PropTypes.func,
  setFilterDict: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
};

export default DocumentFiltersDrawer;